import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useEntityFilter } from '../context/EntityFilterContext';
import TicketPreviewPanel from '../components/TicketPreviewPanel';
import DashboardView from './Dashboard.view';

// Raw HubSpot entity property values → display names (kept in sync with TicketList).
const HS_ENTITY_DISPLAY = {
  'abraaj_water':        'Abraaj Water',
  'abraaj-water':        'Abraaj Water',
  'lofat_subscriptions': 'Lofat Lifestyle',
  'lofat_restaurants':   'Lofat Lifestyle',
  'lofat-lifestyle':     'Lofat Lifestyle',
  'lofat_lifestyle':     'Lofat Lifestyle',
};

// Ticket pipeline IDs → entity name (fallback for HubSpot-only tickets without
// an 'entity' property but with hs_pipeline).
const PIPELINE_ENTITY_NAME = {
  '3765774551': 'Abraaj Water',
  '3765617862': 'Lofat Lifestyle',
};

// Merge portal recent-tickets (from /stats) with HubSpot tickets, dedup on
// hubspot_ticket_id, then apply per-entity windowing (5 per entity, max 20).
function buildRecentMerged(portalTickets, hsTickets) {
  const coveredHsIds = new Set(
    (portalTickets || [])
      .filter((t) => t.hubspot_ticket_id)
      .map((t) => String(t.hubspot_ticket_id))
  );

  const portalRows = (portalTickets || []).map((t) => ({
    _key:         `portal-${t.id}`,
    source:       'portal',
    portalId:     t.id,
    ref:          t.ticket_number,
    subject:      t.subject,
    customerName: t.customer_name,
    entityName:   t.entity_name || '',
    priority:     t.complaint_priority,
    status:       t.status,
    sortDate:     new Date(t.created_at),
    displayDate:  t.created_at,
  }));

  // HubSpot-only rows. All fields required by TicketPreviewPanel are passed
  // through so the slide-over panel can render the full ticket detail +
  // trigger enrichment.
  const hsOnlyRows = (hsTickets || [])
    .filter((ht) => !coveredHsIds.has(String(ht.id)))
    .map((ht) => {
      const entityName =
        HS_ENTITY_DISPLAY[ht.entity] ||
        PIPELINE_ENTITY_NAME[ht.pipelineId] ||
        ht.entity || '';
      return {
        _key:               `hs-${ht.id}`,
        source:             'hubspot',
        portalId:           null,
        hubspotId:          ht.id,
        hubspotTicketRef:   ht.ticketRef,
        portalTicketNumber: null,
        ref:                ht.ticketRef,
        subject:            ht.subject,
        complaintGroup:     ht.complaintGroup || '',
        complaintType:      ht.complaintType  || '',
        channelSource:      ht.channelSource  || '',
        customerName:       ht.customer_name  || '',
        customerMobile:     ht.customer_mobile || '',
        entityName,
        priority:           ht.priority,
        status:             '',
        assignedToName:     '',
        description:        ht.content || '',
        sortDate:           ht.createdAt ? new Date(ht.createdAt) : new Date(0),
        displayDate:        ht.createdAt || '',
        createdAt:          ht.createdAt || '',
      };
    });

  const combined = [...portalRows, ...hsOnlyRows];
  combined.sort((a, b) => b.sortDate - a.sortDate);

  // Per-entity windowing: keep the 5 most recent per entity, hard cap 20.
  const entityCounts = {};
  const windowed = [];
  for (const row of combined) {
    const key = row.entityName || '_unknown';
    entityCounts[key] = (entityCounts[key] || 0) + 1;
    if (entityCounts[key] <= 5) windowed.push(row);
  }
  return windowed.slice(0, 20);
}

// Time-of-day greeting (5–11 morning, 12–17 afternoon, else evening).
function computeGreeting(date) {
  const h = date.getHours();
  if (h >= 5 && h <= 11) return 'morning';
  if (h >= 12 && h <= 17) return 'afternoon';
  return 'evening';
}

// Stat-driven header tagline. Role-aware, zero-suppressing, with empty-state
// copy when there is nothing open.
function computeTagline({ user, stats, entityCount }) {
  if (!stats) return '';
  const byStatus = (status) =>
    parseInt(stats.byStatus?.find((s) => s.status === status)?.count || 0, 10);
  const totalOpen = ['new', 'escalated', 'in_progress', 'pending_third_party']
    .reduce((acc, s) => acc + byStatus(s), 0);
  const escalated = byStatus('escalated');
  const pendingFinance = byStatus('pending_third_party');
  const isSuper = user?.role === 'super_admin';
  const isFinance = (user?.department || '').toLowerCase() === 'finance';
  const entityName = user?.entity_name;

  if (totalOpen === 0) {
    return isSuper
      ? `All clear across ${entityCount || 3} entities.`
      : `All clear on ${entityName || 'your queue'}.`;
  }

  let scope = isSuper
    ? `${totalOpen} open across ${entityCount || 3} entities`
    : `${totalOpen} open on ${entityName || 'your queue'}`;
  if (escalated > 0) scope += ` · ${escalated} escalated`;
  if (isFinance && pendingFinance > 0) scope += ` · ${pendingFinance} awaiting finance`;
  return scope;
}

export default function Dashboard() {
  const { user } = useAuth();
  const { selectedEntity } = useEntityFilter();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [hsTickets, setHsTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hsLoading, setHsLoading] = useState(false);
  const [previewRow, setPreviewRow] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedEntity && selectedEntity !== 'all') params.entity = selectedEntity;
      const res = await api.get('/tickets/stats', { params });
      setStats(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // HubSpot tickets in parallel. Skipped entirely for dept_manager / dept_user
  // (they see only portal tickets escalated to their department via /stats).
  // Prefer the header selector; fall back to the user's single-entity slug.
  const loadHsTickets = async () => {
    if (user?.role === 'dept_manager' || user?.role === 'dept_user') {
      setHsTickets([]);
      return;
    }
    setHsLoading(true);
    try {
      const params = { limit: 50 };
      const eff = (selectedEntity && selectedEntity !== 'all')
        ? selectedEntity
        : (user?.entity_slug || null);
      if (eff) params.entity = eff;
      const res = await api.get('/hubspot/tickets', { params });
      setHsTickets(res.data.tickets || []);
    } catch {
      setHsTickets([]);
    } finally {
      setHsLoading(false);
    }
  };

  // Reload when the header entity selection changes.
  useEffect(() => {
    load();
    loadHsTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEntity]);

  const recentMerged = useMemo(
    () => buildRecentMerged(stats?.recentTickets, hsTickets),
    [stats?.recentTickets, hsTickets]
  );

  // Distinct entities represented in the current stats window — used for the
  // super_admin tagline. Falls back to 3 (Abraaj/Lofat/Regency) if the window
  // hasn't seen every entity yet.
  const entityCount = useMemo(() => {
    const s = new Set(
      (stats?.recentTickets || [])
        .map((t) => t.entity_name)
        .filter(Boolean)
    );
    return s.size || 0;
  }, [stats?.recentTickets]);

  const greetingWord = computeGreeting(new Date());
  const tagline = computeTagline({ user, stats, entityCount });
  const criticalCount = parseInt(
    stats?.byPriority?.find((p) => p.complaint_priority === 'critical')?.count || 0,
    10
  );

  const handleRefresh = () => { load(); loadHsTickets(); };
  const handleOpenPortalTicket = (id) => navigate(`/tickets/${id}`);
  const handleOpenHsPreview = (row) => setPreviewRow(row);

  return (
    <>
      <DashboardView
        user={user}
        stats={stats}
        recent={recentMerged}
        hsLoading={hsLoading}
        loading={loading}
        onRefresh={handleRefresh}
        onOpenPortalTicket={handleOpenPortalTicket}
        onOpenHsPreview={handleOpenHsPreview}
        greetingWord={greetingWord}
        tagline={tagline}
        criticalCount={criticalCount}
      />
      {previewRow && (
        <TicketPreviewPanel row={previewRow} onClose={() => setPreviewRow(null)} />
      )}
    </>
  );
}
